import { useCallback, useMemo, useRef } from 'react';

export interface EchoGuard<T> {
  // Does `value` match the last value known to be agreed between the form
  // and the map? Used from both sides of the sync:
  //  - form → map: skip pushing a value that's already what the map was
  //    last told (breaks the reverse-geocode-writes-address → address-sync
  //    forward-geocodes-it-again loop).
  //  - map → form: only accept an incoming echo when the *live* form value
  //    still matches the last synced one — if it doesn't, the user has
  //    changed the field since and that edit hasn't been pushed yet, so the
  //    echo is stale relative to it and must not clobber it.
  isSynced: (value: T) => boolean;
  recordSynced: (value: T) => void;
}

/**
 * Tracks the last value known to be in agreement between a form field group
 * and an external, asynchronously-updating source (here: an interactive map
 * that echoes address/coordinate changes back after a geocode round-trip).
 *
 * This replaces boolean "suppress the next echo" flags, which get stuck
 * forever if the async operation they're waiting on (a geocode request)
 * never calls back — e.g. it fails, times out, or resolves with no usable
 * result. Comparing against the last known value instead is self-healing:
 * every decision is a fresh comparison, so there's no flag state to leak.
 */
export const useEchoGuard = <T>(
  initialValue: T,
  isEqual: (a: T, b: T) => boolean,
): EchoGuard<T> => {
  const syncedValueRef = useRef<T>(initialValue);

  const isSynced = useCallback(
    (value: T) => isEqual(syncedValueRef.current, value),
    [isEqual],
  );

  const recordSynced = useCallback((value: T) => {
    syncedValueRef.current = value;
  }, []);

  // Stable object identity so consumers (handleAddressChange's useCallback,
  // the sync effects' dependency arrays) don't get invalidated on every
  // render — that would otherwise cascade into the SDK re-registering its
  // autocomplete listener on every keystroke.
  return useMemo(() => ({ isSynced, recordSynced }), [isSynced, recordSynced]);
};
